import { useEffect, useState } from 'react';
import { qd, apiError } from '../api';
import { useQdAuth } from '../QdApp';

const EMPTY = { name: '', country: 'LT', language: 'lt', vat_number: '', reg_code: '', address: '', contact_name: '', contact_email: '', phone: '', website: '' };

export default function QdClients() {
  const { user } = useQdAuth();
  const isAdmin = ['admin', 'superadmin'].includes(user.role);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState('');

  const load = () => qd.get('/clients').then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setMsg('');
    try {
      if (form.id) await qd.patch(`/clients/${form.id}`, form);
      else await qd.post('/clients', form);
      setForm(null); load();
    } catch (e) { setMsg(apiError(e)); }
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Delete client ${c.name}? This cannot be undone.`)) return;
    setMsg('');
    try {
      await qd.delete(`/clients/${c.id}`);
      load();
    } catch (e) { setMsg(apiError(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-[#14233F]">Clients</h1>
        <button onClick={() => setForm({ ...EMPTY })} className="bg-[#0F7D70] text-white px-4 py-2 rounded text-sm font-semibold" data-testid="qd-new-client-btn">New client</button>
      </div>

      {msg && !form && <p className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded px-4 py-2.5" data-testid="qd-clients-msg">{msg}</p>}

      {form && (
        <div className="bg-white border border-slate-200 rounded p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="qd-client-form">
          <input value={form.name} onChange={set('name')} placeholder="Company name *" className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="qd-client-name" />
          <select value={form.country} onChange={set('country')} className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="qd-client-country">
            {['LT', 'LV', 'EE', 'PL', 'FI', 'SE', 'DE', 'FR', 'Other EU'].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input value={form.vat_number} onChange={set('vat_number')} placeholder="VAT number" className="border border-slate-300 rounded px-3 py-2 text-sm mono" data-testid="qd-client-vat" />
          <input value={form.reg_code || ''} onChange={set('reg_code')} placeholder="Company reg. code" className="border border-slate-300 rounded px-3 py-2 text-sm mono" data-testid="qd-client-regcode" />
          <input value={form.address} onChange={set('address')} placeholder="Address" className="border border-slate-300 rounded px-3 py-2 text-sm md:col-span-2" data-testid="qd-client-address" />
          <input value={form.contact_name} onChange={set('contact_name')} placeholder="Contact name" className="border border-slate-300 rounded px-3 py-2 text-sm" />
          <input value={form.contact_email} onChange={set('contact_email')} placeholder="Contact email" className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="qd-client-email" />
          <input value={form.phone || ''} onChange={set('phone')} placeholder="Phone" className="border border-slate-300 rounded px-3 py-2 text-sm mono" data-testid="qd-client-phone" />
          <input value={form.website || ''} onChange={set('website')} placeholder="Website" className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="qd-client-website" />
          <div className="flex gap-2 md:col-span-2">
            <button onClick={save} className="bg-[#14233F] text-white px-4 py-2 rounded text-sm" data-testid="qd-client-save">Save</button>
            <button onClick={() => setForm(null)} className="border border-slate-300 px-4 py-2 rounded text-sm text-slate-600">Cancel</button>
            {msg && <span className="text-xs text-red-600 self-center">{msg}</span>}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Country</th>
              <th className="text-left px-4 py-3">VAT</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                <td className="px-4 py-3 mono text-xs">{c.country}</td>
                <td className="px-4 py-3 mono text-xs text-slate-500">{c.vat_number}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{c.contact_name} {c.contact_email && `· ${c.contact_email}`}</td>
                <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                  <button onClick={() => setForm(c)} className="text-xs text-[#0F7D70] hover:underline" data-testid={`qd-client-edit-${c.id}`}>Edit</button>
                  {isAdmin && <button onClick={() => deleteClient(c)} className="text-xs text-red-600 hover:underline" data-testid={`qd-client-delete-${c.id}`}>Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
